import { useEffect } from "react";
import { batch, useDispatch } from "react-redux";
import { useSocket } from "../../../../../connection/socket";
import { setModuleQuestions, setModuleQuestionsComments } from "../../../../../store/actions/event/questions-actions";
import { BlProfile, IQuestion, ISocketCommentProps, PageModule, SqsSocketTypes, Status } from "../../../../../types/working-model";
import { binarySearchNumbers } from "../../../../../utils/utils";
import { QUESTIONS_PAGE_LIMIT } from "./questions";

interface IUseQuestionsSocketsProps {
	session_uuid: string;
	language: string;
	questions: {
		[key: number]: Map<number, IQuestion>;
	};
	comments: {
		[key: number]: {
			[key: number]: Map<number, ISocketCommentProps>;
		};
	};
	blProfileUser: BlProfile | null;
	module?: PageModule;
}

const useQuestionsSockets = ({ session_uuid, language, questions, comments, blProfileUser, module }: IUseQuestionsSocketsProps): void => {
	const questionSocket = useSocket(`session-${session_uuid}-${language}`);

	const dispatch = useDispatch();

	useEffect(() => {
		const handleQuestionMessage = (message: any) => {
			message?.chat_messages?.forEach((msg: IQuestion) => {
				if (module?.id) {
					const copy = Array.from(new Map(questions[module.id]));
					copy.unshift([msg.id, msg]);
					const updated = new Map(copy);
					dispatch(setModuleQuestions({ [module.id]: updated }));
				}
			});
		};

		const handleQuestionCommentMessage = (message: any) => {
			// add comment to question comments list for rendering.
			// chat_messages length is always one, so it doesn't matter that we're calling
			// the dispatch inside the loop.
			const handleIncomingMessage = (msg: ISocketCommentProps) => {
				if (module?.id && comments[module.id]?.[msg?.question_id]) {
					let commentsCopy = new Map(comments[module.id][msg.question_id]);
					// update the matched comment list
					let arrayedComments = Array.from(commentsCopy);
					// do not list more than QUESTIONS_PAGE_LIMIT messages to save the user browser memory
					if (arrayedComments.length > QUESTIONS_PAGE_LIMIT) {
						arrayedComments = arrayedComments.slice(0, QUESTIONS_PAGE_LIMIT);
					}
					// update map by putting new message at beginning
					commentsCopy = new Map([[msg.id, msg], ...arrayedComments]);
					dispatch(setModuleQuestionsComments(module.id, msg.question_id, commentsCopy));

					// increment question comments length in place
					const updatedQns = new Map(questions[module.id]);
					const qn = updatedQns.get(msg.question_id);
					if (qn) {
						updatedQns.set(qn.id, {
							...qn,
							total_comments: qn?.total_comments ? Number(qn?.total_comments) + 1 : 1,
						});
					}
					dispatch(setModuleQuestions({ [module.id]: updatedQns }));
				}
			};

			// if message.data, then it's a moderator message
			if (message?.data) {
				const msg: ISocketCommentProps = message.data;
				handleIncomingMessage(msg);
			} else {
				// else it's a queue message
				message.chat_messages.forEach((message: ISocketCommentProps) => {
					handleIncomingMessage(message);
				});
			}
		};

		const handleQuestionLikeMessage = (message: any) => {
			if (!module?.id) return;
			const copy = new Map(questions[module.id]);
			message.chat_messages.forEach((msg: any) => {
				const qn = copy.get(msg.question_id);
				if (qn) {
					if (msg?.user?.bl_profile?.toString() === blProfileUser?.bl_profile?.toString()) {
						copy.set(qn.id, {
							...qn,
							has_liked: true,
							likes: Number(qn?.likes ?? 0) + 1,
						});
					} else {
						copy.set(qn.id, {
							...qn,
							likes: Number(qn?.likes ?? 0) + 1,
						});
					}
				}
			});
			dispatch(setModuleQuestions({ [module.id]: copy }));
		};

		const handleQuestionUnlikeMessage = (message: any) => {
			if (!module?.id) return;
			const copy = new Map(questions[module.id]);
			message.chat_messages.forEach((msg: any) => {
				const qn = copy.get(msg.question_id);
				if (qn) {
					if (msg?.user?.bl_profile?.toString() === blProfileUser?.bl_profile?.toString()) {
						copy.set(qn.id, {
							...qn,
							has_liked: false,
							likes: Number(qn?.likes ?? 0) - 1,
						});
					} else {
						copy.set(qn.id, {
							...qn,
							likes: Number(qn?.likes ?? 0) - 1,
						});
					}
				}
			});
			dispatch(setModuleQuestions({ [module.id]: copy }));
		};

		const updateQuestionStatus = (msg: any) => {
			// update question
			const data = msg?.data;
			if (!data || !module?.id) return;
			let questionsCopy = new Map(questions[module.id]);
			const questionCopy = questionsCopy.get(data.id);
			// only update the comment if it exists
			if (!questionCopy) {
				// we should insert at the beginning of the set because we want newly approved messages
				// to pop up at the top of the list
				const arrayed = Array.from(questionsCopy);
				arrayed.unshift([data.id, data]);
				questionsCopy = new Map(arrayed);
			} else {
				questionsCopy.set(questionCopy.id, {
					...questionCopy,
					...data,
					created_at: questionCopy.created_at, // to ensure date doesn't get messed up
				});
			}
			dispatch(setModuleQuestions({ [module.id]: questionsCopy }));
		};

		const deleteQuestion = (msg: any) => {
			const data = msg?.data;
			if (!data || !module?.id) return;
			// delete the question
			const questionsCopy = new Map(questions[module.id]);
			const questionCopy = questionsCopy.get(data.id);
			if (questionCopy) {
				questionsCopy.delete(questionCopy.id);
			}

			batch(() => {
				// remove the question
				dispatch(setModuleQuestions({ [Number(module.id)]: questionsCopy }));
				// delete associated comments
				dispatch(setModuleQuestionsComments(Number(module.id), data.id, new Map()));
			});
		};

		const unDeleteQuestion = () => {
			//
		};

		const updateQuestionCommentStatus = (msg: any) => {
			// update question
			const data = msg?.data;
			if (!data) return;
			const { question_id, id, module_id } = data;
			if (comments[module_id][question_id]) {
				let commentsCopy = new Map(comments[module_id][question_id]);
				const commentCopy = commentsCopy.get(id);
				if (!commentCopy) {
					// If the incoming message is not in the list we need to make sure we insert it
					// at the correct position - that is, next to the closest matching id, otherwise the
					// list will not be in proper "chatting" order
					const arrayed = Array.from(commentsCopy);
					const closestId = binarySearchNumbers(Array.from(commentsCopy.keys()).reverse(), data.id);
					if (closestId) {
						const indexOfClosest = arrayed.findIndex((data => data[0] === closestId));
						if (indexOfClosest === 0) {
							// if index of closest is first element, just add to start of array
							arrayed.unshift([data.id, data]);
						} else if (indexOfClosest === arrayed.length - 1) {
							// if index of closest is last element, just add to end of array
							arrayed.push([data.id, data]);
						} else {
							arrayed.splice(indexOfClosest, 0, [data.id, data]);
						}
						commentsCopy = new Map(arrayed);
					} else {
						// just go ahead and insert since it's the first message
						commentsCopy.set(data.id, data);
					}
				} else {
					commentsCopy.set(commentCopy.id, {
						...commentCopy,
						...data,
					});
				}
				dispatch(setModuleQuestionsComments(module_id, question_id, commentsCopy));
			}
		};


		const deleteQuestionComment = (msg: any) => {
			// update list and hide question comment
			const data = msg?.data;
			if (!data) return;
			const { comment_id, question_id, module_id } = data;
			if (comments?.[module_id]?.[question_id]) {
				const commentsCopy = new Map(comments[module_id][question_id]);
				const commentCopy = commentsCopy.get(comment_id);
				if (!commentCopy) return;
				commentsCopy.set(commentCopy.id, {
					...commentCopy,
					status: Status.Deleted,
				});
				dispatch(setModuleQuestionsComments(module_id, question_id, commentsCopy));
			}
		};

		const unDeleteQuestionComment = (msg: any) => {
			// update list and hide question comment

			const data = msg?.data;
			if (!data) return;
			const { id, question_id, module_id } = data;
			if (comments?.[module_id]?.[question_id]) {
				const commentsCopy = new Map(comments[module_id][question_id]);
				const commentCopy = commentsCopy.get(id);
				if (!commentCopy) return;
				commentsCopy.set(commentCopy.id, {
					...commentCopy,
					status: Status.Unapproved,
				});
				dispatch(setModuleQuestionsComments(module_id, question_id, commentsCopy));
			}
		};

		// from lambda
		questionSocket.addListener(SqsSocketTypes.QUESTION, handleQuestionMessage);
		questionSocket.addListener(SqsSocketTypes.QUESTION_COMMENT, handleQuestionCommentMessage);
		questionSocket.addListener(SqsSocketTypes.QUESTION_LIKE, handleQuestionLikeMessage);
		questionSocket.addListener(SqsSocketTypes.QUESTION_UNLIKE, handleQuestionUnlikeMessage);

		// moderator updates
		questionSocket.addListener('approve_question', updateQuestionStatus);
		questionSocket.addListener('unapprove_question', updateQuestionStatus);
		questionSocket.addListener('delete_question', deleteQuestion);
		questionSocket.addListener('undelete_question', unDeleteQuestion);
		questionSocket.addListener('approve_question_comment', updateQuestionCommentStatus);
		questionSocket.addListener('unapprove_question_comment', updateQuestionCommentStatus);
		questionSocket.addListener('delete_question_comment', deleteQuestionComment);
		questionSocket.addListener('undelete_question_comment', unDeleteQuestionComment);
		return () => {
			// remove socket listeners
			questionSocket.removeListener(SqsSocketTypes.QUESTION, handleQuestionMessage);
			questionSocket.removeListener(SqsSocketTypes.QUESTION_COMMENT, handleQuestionCommentMessage);
			questionSocket.removeListener(SqsSocketTypes.QUESTION_LIKE, handleQuestionLikeMessage);
			questionSocket.removeListener(SqsSocketTypes.QUESTION_UNLIKE, handleQuestionUnlikeMessage);

			questionSocket.removeListener('approve_question', updateQuestionStatus);
			questionSocket.removeListener('unapprove_question', updateQuestionStatus);
			questionSocket.removeListener('delete_question', deleteQuestion);
			questionSocket.removeListener('undelete_question', unDeleteQuestion);
			questionSocket.removeListener('approve_question_comment', updateQuestionCommentStatus);
			questionSocket.removeListener('unapprove_question_comment', updateQuestionCommentStatus);
			questionSocket.removeListener('delete_question_comment', deleteQuestionComment);
			questionSocket.removeListener('undelete_question_comment', unDeleteQuestionComment);
		};
	}, [blProfileUser?.bl_profile, questionSocket, dispatch, module?.id, questions, comments]);
};

export default useQuestionsSockets;
